import React, { useCallback, useState, useEffect } from 'react';
import { ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { useModal } from '@jutro/components';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { LoadSaveService } from 'gw-capability-gateway-quoteandbind';
import { FormModalPopover, platformMessages } from 'ml-components-platform-react';
import { MLMultiLobsDataHolderService } from 'ml-services-common-react';
import { MLModalService } from 'ml-portals-services-react';
import { MLHVQDiscrepanciesUtil, MLProductCodeUtil, MLErrorMessageUtil } from 'ml-portals-util-js';
import _ from 'lodash';

import mlHvqDiscrepanciesCheckPopup from './MLHVQDiscrepanciesCheckPopup.metadata.json5';
import mlHvqDiscrepanciesCheckPopupMsg from './MLHVQDiscrepanciesCheckPopup.messages';
import mlHvqDiscrepanciesCheckPopupStyles from './MLHVQDiscrepanciesCheckPopup.module.scss';

import metadata from './MLHVQDiscrepanciesCheckPage.metadata.json5';

import HOWizardConfig from '../../../gw-capability-quoteandbind-ho-react/config/ho-wizard-config.json5';

function MLHVQDiscrepanciesCheckPage(props) {
    const modalApi = useModal();
    const {
        wizardData: submissionVM,
        updateWizardData,
        goNext,
        steps,
        isSkipping,
        changeNextSteps,
        currentStep
    } = props;
    const translator = useTranslator();
    const { authHeader } = useAuthentication();

    const [pendingDiscrepancyitemVMs, setPendingDiscrepancyitemVMs] = useState([]);
    const [fetchedDiscrepancyItemsFlag, updateFetchedDiscrepancyItemsFlag] = useState(false);

    const {
        showProgressDialog,
        hideProgressDialog
    } = MLModalService();

    const {
        getLOBPendingDiscrepancyItemVMs,
        getLOBDiscrepancyItemVMs,
        markAllDiscrepancyItemsCompleted,
        getPropertyVMsFromVMItemWithDefaultSelection,
        markDiscrepancyItemCompleted
    } = MLHVQDiscrepanciesUtil();

    const { refreshData } = useDataRefresh();

    const hasDiscrepancies = useCallback(() => {
        return pendingDiscrepancyitemVMs.length > 0;
    }, [pendingDiscrepancyitemVMs]);

    const getNextDiscrepancy = useCallback(() => {
        return pendingDiscrepancyitemVMs.shift();
    }, [pendingDiscrepancyitemVMs]);

    const resolveDiscrepancies = useCallback((onResolvedDiscrepancies) => {
        const promise = LoadSaveService.resolveHVQDiscrepancies(submissionVM.value, authHeader);
        showProgressDialog(platformMessages.saveSubmission,promise);
        promise.then((submissionModel) => {
            onResolvedDiscrepancies(submissionModel);
        }).catch(error => {
            // US1172145: Handle CDCE Exceptions error
            hideProgressDialog();
            MLErrorMessageUtil.handleException(error, mlHvqDiscrepanciesCheckPopupMsg.somethingWentWrong);
        });
    }, [authHeader, showProgressDialog, submissionVM.value]);

    const goToNext = useCallback(() => {
        const productCode = _.get(submissionVM, 'baseData.productCode.value', '');
        if (MLProductCodeUtil.isHomeOwners(productCode)) {
            let hoNextStepName;
            const coverageType = _.get(submissionVM, 'lobData.homeowners.coverables.coveragePartType_ML.value.code', undefined);
            if (coverageType === 'hoprental') {
                const index = _.findIndex(
                    steps,
                    ({ path }) => path === '/construction'
                );
                if (index > 0) {
                    const remainingSteps = _.slice(steps, index + 1);
                    changeNextSteps(remainingSteps);
                }
                hoNextStepName = 'hoRating';
            } else {
                const hasConstructionStep = steps.some((step) => step.path === '/construction');
                if (!hasConstructionStep) {
                    const wizardSteps = HOWizardConfig.steps;
                    const constructionIndex = _.findIndex(
                        wizardSteps, ({ path }) => path === '/construction'
                    );
                    const nextSteps = _.slice(wizardSteps, constructionIndex);
                    changeNextSteps(nextSteps);
                }
                hoNextStepName = 'hoConstruction';
            }
            MLMultiLobsDataHolderService.storeFlowSteps(currentStep.mlStepName, hoNextStepName, authHeader);
        } else {
            const paNextStepName = 'paPreFillData';
            MLMultiLobsDataHolderService.storeFlowSteps(currentStep.mlStepName, paNextStepName, authHeader);
        }
        goNext();
    }, [authHeader, changeNextSteps, currentStep.mlStepName, goNext, steps, submissionVM]);

    let showModalOrGoToNext;

    const showDiscrepancyModal = useCallback(() => {
        const discrepancyItemVM = getNextDiscrepancy();
        const discrepancyPropertyVMs = getPropertyVMsFromVMItemWithDefaultSelection(discrepancyItemVM);

        const onClickContinue = () => {
            markDiscrepancyItemCompleted(discrepancyItemVM);

            const isLastDiscrepancy = !hasDiscrepancies();
            if (isLastDiscrepancy) {
                showModalOrGoToNext();
            } else {
                resolveDiscrepancies(() => {
                    showModalOrGoToNext();
                });
            }
            refreshData();
            return true;
        };

        const overrides = {
            discrepancyItemTitle: {
                content: _.get(discrepancyItemVM, 'title.value', '')
            },
            discrepancyPropertyGroup: {
                discrepancyProperties: discrepancyPropertyVMs,
                showPropertyGroupDoubleBorderStyle: false
            }
        };

        const componentProps = {
            title: translator(mlHvqDiscrepanciesCheckPopupMsg.informationNotMatchRecord),
            uiProps: mlHvqDiscrepanciesCheckPopup.pageContent,
            actionBtnLabel: hasDiscrepancies() ? _.upperCase(translator(platformMessages.mlContinue)) : translator(mlHvqDiscrepanciesCheckPopupMsg.continueWithQuote),
            viewModel: discrepancyItemVM,
            modalBodyStyles: mlHvqDiscrepanciesCheckPopupStyles,
            overrideProps: overrides,
            onSaveAction: onClickContinue,
            showXCloseButton: false,
            messageNextToButton: hasDiscrepancies() ? (<FormattedMessage
                {...mlHvqDiscrepanciesCheckPopupMsg.continueText}
                values={{
                    additionalItems: <strong>{translator(mlHvqDiscrepanciesCheckPopupMsg.additionalItems)}</strong>
                }}
            />) : '',
        };
        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        );
    }, [getNextDiscrepancy, getPropertyVMsFromVMItemWithDefaultSelection, hasDiscrepancies, markDiscrepancyItemCompleted, refreshData, resolveDiscrepancies, showModalOrGoToNext, translator]);

    showModalOrGoToNext = useCallback(() => {
        if (hasDiscrepancies()) {
            showDiscrepancyModal();
        } else {
            // Always tell the backend that discrepancies have been resolved (even if none were present)
            // Otherwise, when going back in the flow it would interpret the new changes as discrepancies
            markAllDiscrepancyItemsCompleted(submissionVM);
            resolveDiscrepancies((submissionModel) => {
                _.set(submissionVM, 'value', submissionModel);
                goToNext();
            });
        }
    }, [hasDiscrepancies, showDiscrepancyModal, markAllDiscrepancyItemsCompleted, submissionVM, resolveDiscrepancies, goToNext]);

    const skipOnResumeIf = useCallback(() => {
        // Skip this page if quote is not HVQ.
        if (!(submissionVM.hvqQuoteID_ML && submissionVM.hvqQuoteID_ML.value)) {
            return true;
        }

        if (!!_.get(submissionVM, 'bindData.policyNumber.value')
            && (submissionVM.value.baseData.status_ML === 'Bound' || submissionVM.value.baseData.status_ML === 'Binding')) {
            return true;
        }

        if (MLMultiLobsDataHolderService.getLastVisitedStep() === 'quoteDetails' &&
            submissionVM.hvqQuoteID_ML.value &&
            submissionVM.isBundle_ML.value) {
            return true;
        }

        return MLMultiLobsDataHolderService.hasCompletedStep(currentStep.mlStepName);
    }, [currentStep, submissionVM]);

    useEffect(() => {
        if (!isSkipping || (isSkipping && !skipOnResumeIf())) {
            const discrepancyItemVMs = getLOBDiscrepancyItemVMs(submissionVM);
            if (discrepancyItemVMs.length) {
                const lobPendingDiscrepancyItemVMs = getLOBPendingDiscrepancyItemVMs(submissionVM);
                setPendingDiscrepancyitemVMs([...pendingDiscrepancyitemVMs, ...lobPendingDiscrepancyItemVMs]);
                updateFetchedDiscrepancyItemsFlag(true);
            } else {
                goToNext();
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isSkipping || (isSkipping && !skipOnResumeIf())) {
            if (fetchedDiscrepancyItemsFlag) {
                showModalOrGoToNext();
                updateFetchedDiscrepancyItemsFlag(false);
            }
        }
    }, [fetchedDiscrepancyItemsFlag, hasDiscrepancies, isSkipping, showModalOrGoToNext, skipOnResumeIf]);

    return (
        <WizardPage
            skipWhen={skipOnResumeIf}
            showCancel={false}
            showPrevious={false}
            showNext={false}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                onModelChange={updateWizardData}
            />
        </WizardPage>
    );
}

MLHVQDiscrepanciesCheckPage.propTypes = wizardProps;
export default MLHVQDiscrepanciesCheckPage;